// @see: https://swr.vercel.app/examples/infinite-loading
import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useIntersection, usePrevious } from "react-use"
import useSWRInfinite from "swr/infinite"

import { Paging } from "types"
import { useLoading } from "./useLoading"
import { http } from "../../libs/http/http"
import { getTokenHeader } from "../../libs/http/lib/get-token-header"

type KeyLoader = (
  index: number,
  previousPageData: unknown | null,
) => string | unknown[] | null

const OPTIONS = {}

/**
 * @deprecated ロード部分だけをまとめたuseInfinityLoadを利用する
 */
export const useLoadMore = <T>(getKey: KeyLoader) => {
  const { fullScreenLoading } = useLoading()
  const intersectionRef = useRef<HTMLButtonElement | null>(null)

  const [hasNext, setHasNext] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const intersection = useIntersection(intersectionRef, OPTIONS)
  const prevIntersection = usePrevious(intersection)

  const fetcher = useCallback(
    async (url: string) => {
      fullScreenLoading(true)
      const tokenHeader = await getTokenHeader()

      const { next, results, count } = await http
        .get<Paging<T>>(url, { headers: { ...tokenHeader } })
        .then((res) => res.data)
      setHasNext(next)
      setTotalCount(count)
      fullScreenLoading(false)
      setHasLoaded(true)

      return results
    },
    [fullScreenLoading],
  )

  const { data, size, setSize, mutate } = useSWRInfinite<T[]>(getKey, fetcher)

  const flattenData = useMemo(() => {
    return data ? data.flat() : []
  }, [data])

  const isLoadingMore = useMemo(() => {
    return size > 0 && data && typeof data[size - 1] === "undefined"
  }, [data, size])

  const onLoadMore = useCallback(() => {
    if (isLoadingMore) {
      return
    }
    setSize((size) => size + 1)
  }, [isLoadingMore, setSize])

  useEffect(() => {
    if (
      hasNext &&
      intersection?.isIntersecting &&
      !prevIntersection?.isIntersecting
    ) {
      onLoadMore()
    }
  }, [
    hasNext,
    intersection?.isIntersecting,
    onLoadMore,
    prevIntersection?.isIntersecting,
  ])

  return {
    hasLoaded,
    data: flattenData,
    totalCount,
    hasNext,
    isLoadingMore,
    intersectionRef,
    onLoadMore,
    setHasLoaded,
    mutate,
  }
}
